/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { Fragment } from 'react';

import { jsx } from '@compiled/react';

import Link from '@atlaskit/link';
import { fg } from '@atlaskit/platform-feature-flags';

import {
	type NLPSearchResultSource,
} from '../../../ai-answer-dialog/types';
import { extractUrlHostname } from '../../../utils';
import { ErrorBoundary } from '../error-boundary';

const Card = React.lazy(() =>
	import(/* webpackChunkName: "@atlaskit-internal_.smart-card" */ '@atlaskit/smart-card').then(
		({ Card }) => ({
			default: Card,
		}),
	),
);

const resolveUrlPlaceholder = (url: string) => {
	const textFragmentRegex = new RegExp('#:~:text=.*$', 'g');
	const urlWithoutTextFragment = url?.replace(textFragmentRegex, '');
	const maxLength = 30;
	const truncatedUrl =
		urlWithoutTextFragment.length > maxLength
			? urlWithoutTextFragment.slice(0, maxLength) + '...'
			: urlWithoutTextFragment;
	return truncatedUrl;
};

const constructSourceUrl = ({
	source,
	searchSessionId,
}: {
	source: NLPSearchResultSource;
	searchSessionId: string;
}) => {
	if (source.product === 'Confluence') {
		const url = new URL(source.url);
		url.searchParams.append('search_id', searchSessionId);
		return url.toString();
	} else {
		return source.url;
	}
};

const fallbackComponent =
	({
		index,
		source,
		searchSessionId,
		onClickHandler,
	}: Omit<SourceCardProps, 'enableSmartLinkSources'>) =>
		() => {
			return fg('dst-a11y__replace-anchor-with-link__knowledge-disc') ? (
				<Link
					href={constructSourceUrl({ source, searchSessionId })}
					onClick={() => onClickHandler(index, source, extractUrlHostname(source.url))}
					rel="noopener noreferrer"
					target="_blank"
				>
					{source.title ?? source.url}
				</Link>
			) : (
				// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
				<a
					href={constructSourceUrl({ source, searchSessionId })}
					onClick={() => onClickHandler(index, source, extractUrlHostname(source.url))}
					rel="noopener noreferrer"
					target="_blank"
				>
					{source.title ?? source.url}
				</a>
			);
		};

type SourceCardProps = {
	index: number;
	source: NLPSearchResultSource;
	searchSessionId: string;
	onClickHandler: (index: number, source: NLPSearchResultSource, hostname?: string) => void;
	enableSmartLinkSources: boolean;
};

export const SourceCard = ({
	index,
	source,
	searchSessionId,
	onClickHandler,
	enableSmartLinkSources,
}: SourceCardProps): React.JSX.Element => {
	const SmartLinkSource = (
		<Card
			appearance="inline"
			url={source.url}
			removeTextHighlightingFromTitle
			showHoverPreview
			fallbackComponent={fallbackComponent({
				index,
				source,
				searchSessionId,
				onClickHandler,
			})}
			onClick={(e) => {
				onClickHandler(index, source, extractUrlHostname(source.url));
				e.preventDefault();
				window.open(
					constructSourceUrl({ source, searchSessionId }),
					'_blank',
					'noopener, noreferrer',
				);
			}}
			onError={() => {
				// Nothing for now
			}}
			resolvingPlaceholder={resolveUrlPlaceholder(source.url)}
			placeholder={resolveUrlPlaceholder(source.url)}
		/>
	);

	return (
		<ErrorBoundary fallback={<Fragment />}>
			{enableSmartLinkSources
				? SmartLinkSource
				: fallbackComponent({ index, source, searchSessionId, onClickHandler })()}
		</ErrorBoundary>
	);
};